import { Chip } from "@gigpro/ui/Chip";
import { Flame } from "@gigpro/ui/Icons";
import { cn } from "@gigpro/ui/tailwind/cn";
import { Tooltip } from "@gigpro/ui/Tooltip";
import { formatCurrency } from "@gigpro/utils";
import React from "react";

interface HighDemandChipProps {
  proName: string;
  /**
   * The pro's pre-multiplier base hourly wage. The chip computes the
   * surcharged rate internally as `baseWage × multiplier`. Pass the frozen
   * base in historical contexts (e.g. payment review) so the displayed math
   * matches the frozen surcharge that was actually billed.
   */
  baseWage: number;
  multiplier: number;
  className?: string;
  /**
   * Overrides the default selection-time tooltip copy. Use in contexts where
   * the multiplier has already been frozen (e.g. payment review) and the
   * "Pick another applicant to avoid the surcharge" phrasing no longer fits.
   */
  tooltipContent?: React.ReactNode;
  /**
   * When true, drop the "High-demand" word and render a glyph-only chip
   * (`🔥 1.5×`). Use in dense list contexts where every visible chip already
   * implies "high demand" and the word would crowd the layout. The full
   * explanation stays accessible via the tooltip.
   */
  compact?: boolean;
  /**
   * When true, render the chip's tooltip trigger as a `<span>` instead of a
   * `<button>`. Use when the chip is nested inside another `<button>` (e.g.
   * a clickable group card) — HTML disallows nested buttons, and the parent
   * stays the canonical click target.
   */
  asInline?: boolean;
  tooltipOpen?: boolean;
  onTooltipOpenChange?: (open: boolean) => void;
}

export const HighDemandChip = ({
  proName,
  baseWage,
  multiplier,
  className,
  tooltipContent,
  compact = false,
  asInline = false,
  tooltipOpen,
  onTooltipOpenChange,
}: HighDemandChipProps) => {
  const multiplierLabel = `${multiplier}×`;
  const surchargedRate = baseWage * multiplier;
  const defaultTooltip = `Selecting ${proName} will bill at the High-demand rate (${formatCurrency(baseWage)}/hr × ${multiplier} = ${formatCurrency(surchargedRate)}/hr). Pick another applicant to avoid the surcharge.`;
  const ariaLabel = `High-demand ${multiplierLabel} surcharge applies`;
  const innerContent = (
    <>
      <Flame className="size-3" aria-hidden="true" />
      {compact ? multiplierLabel : <>High-demand · {multiplierLabel}</>}
    </>
  );

  return (
    <Tooltip content={tooltipContent ?? defaultTooltip} open={tooltipOpen} onOpenChange={onTooltipOpenChange}>
      <Chip asChild className={cn("w-fit bg-accent-light-orange font-regular", className)}>
        {asInline ? (
          <span role="img" aria-label={ariaLabel}>
            {innerContent}
          </span>
        ) : (
          <button type="button" aria-label={ariaLabel}>
            {innerContent}
          </button>
        )}
      </Chip>
    </Tooltip>
  );
};
